import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Pagination from "@/components/base/Pagination";
import { news, newsCategories } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 5;

export default function NewsListPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const [category, setCategory] = useState(initialCategory);
  const [page, setPage] = useState(1);

  usePageMeta(
    "最新影视资讯 - 80s电影",
    "80s电影影视资讯频道，持续更新80年代电影修复、幕后故事与影史回顾内容。",
    "影视资讯,80年代电影,电影资讯,影史回顾"
  );

  const filtered = useMemo(() => {
    if (category === "all") return news;
    return news.filter((item) => item.category === category);
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <PageLayout>
      <div className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6">
        <nav className="mb-2 flex items-center gap-1.5 font-label text-xs text-foreground-500">
          <Link to="/" className="hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">影视资讯</span>
        </nav>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          最新影视动态
        </h1>
        <p className="mt-2 font-label text-sm text-foreground-600">
          关于80年代电影修复、幕后制作与影史回顾的最新资讯
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setCategory("all");
              setPage(1);
            }}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-xs transition-colors ${
              category === "all"
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 text-foreground-600 hover:border-accent-400"
            }`}
          >
            全部栏目
          </button>
          {newsCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => {
                setCategory(cat);
                setPage(1);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-xs transition-colors ${
                category === cat
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-600 hover:border-accent-400"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {pageItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-newspaper-line mb-3 flex h-12 w-12 items-center justify-center text-3xl text-foreground-300"></i>
              <p className="font-label text-sm text-foreground-600">
                暂无相关资讯，试试切换其他栏目
              </p>
            </div>
          ) : (
            pageItems.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 sm:flex-row"
              >
                <div className="h-44 w-full shrink-0 overflow-hidden rounded-md bg-primary-950 sm:h-28 sm:w-40">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} 80s电影资讯`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
                    <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                      {item.category}
                    </span>
                    <span>{item.date}</span>
                  </div>
                  <h2 className="mt-1.5 font-heading text-base font-semibold text-foreground-950 line-clamp-1 group-hover:text-accent-600">
                    {item.title}
                  </h2>
                  <p className="mt-1.5 font-label text-sm text-foreground-600 line-clamp-2">
                    {item.summary}
                  </p>
                </div>
              </Link>
            ))
          )}
        </div>

        {pageItems.length > 0 && (
          <div className="mt-10">
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>
    </PageLayout>
  );
}